<template>
  <section class="recent-actions">
    <div class="additional-actions">
      <div class="mb-4 summary-row" @click="toggleOpen">
        <div class="summary-left">
          <IconProvider name="ChevronDown" v-if="!isOpen" />
          <IconProvider name="ChevronUp" v-else />
          <span class="additional-actions-title ml-2">History</span>
        </div>

        <div class="summary-right">
          <v-menu v-model="filterMenu" location="bottom end" :offset="8">
            <template #activator="{ props }">
              <div v-bind="props" class="filter-label" @click.stop>
                <span>{{ filterMode === 'assignment' ? 'Assignment' : 'All' }}</span>
                <IconProvider :name="filterMenu ? 'ChevronUp' : 'ChevronDown'" class="filter-arrow" />
              </div>
            </template>

            <v-list density="comfortable" nav class="list-container ma-0 px-0">
              <v-list-item title="Assignment" @click="setFilter('assignment')"
                :class="[{ 'filter-active': filterMode === 'assignment' }, 'mb-0 list-item']" />
              <v-list-item title="All" @click="setFilter('all')"
                :class="[{ 'filter-active': filterMode === 'all' }, 'mb-0 list-item']" />
            </v-list>
          </v-menu>
        </div>
      </div>

      <div v-if="isOpen && isLoaded">
        <div class="recent-actions-card" v-if="getFilteredRecentActions?.length > 0">
          <div class="actions-list" @scroll="handleScroll" ref="actionsList">
            <div v-for="action in getFilteredRecentActions" :key="action.id" class="action-item">
              <div class="action-content">
                <div class="action-description mb-2">{{ action.subjectLine }}</div>
                
                <div v-if="action && action.fileId && action.action !== 'Create New Quote'" class="action-title"> 
                <template v-if="action.action === 'Save to Box'">
                  <a
                     v-if="historyAction?.linkConfigs?.[0]?.config"
                    :href="
                      link(historyAction.linkConfigs[0].config, action.assignmentIds[0], action.fileId)
                    "
                    :target="linkTarget(historyAction.linkConfigs[0].config)"
                    class="action-title"
                  >
                    {{ action.fileId }}
                  </a>
                  <span> : Save to </span>
                  <a
                    v-if="getLinkConfig(action)"
                    :href="getLinkHref(action)"
                    :target="linkTarget(getLinkConfig(action))"
                    class="action-title"
                  >
                    Box
                  </a>
                </template>
                <template v-else>
                  <a
                    v-if="getLinkConfig(action)"
                    :href="getLinkHref(action)"
                    :target="linkTarget(getLinkConfig(action))"
                    class="action-title"
                  >
                    {{ action.fileId }}
                  </a>
                  <span> : {{ action.description }}</span>
                  <span v-if="action.action === 'Property Contact Request' && action.propertyContact" class="inline-property-contact">
                    | Property Contact Request sent to {{ action.propertyContact }}
                  </span>
                </template>
              </div>
                <div v-else class="action-title">
                  {{ extendedDescription(action) }}
                  <span v-if="action.action === 'Create New Quote' && action.engagementStatus === 'SUCCESS' && action.engagementId && historyAction?.linkConfigs?.[1]?.config" class="inline-engagement">
                    | <a :href="link(historyAction.linkConfigs[1].config, action.engagementId)" :target="linkTarget(historyAction.linkConfigs[1].config)" class="engagement-link">{{ action.engagementNumber }}</a>
                  </span>
                </div>
              </div>
              <div class="action-date">
                {{ formatDate(action.createdDate) }}
              </div>
            </div>
          </div>
        </div>
        <div class="assignment-bar border-all-dashed" v-else>
          No recent actions. Actions performed will appear here
        </div>
      </div>
      <div class="loading-indicator">
        <IconProvider v-if="isLoading" name="Loader" class="loader animate-spin" />
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import { fetchRecentActions } from '../../service/engagement.js'
import { EventBus } from '../../service/event-bus.js';
import { formatDate as formatDateString } from '@/utils/globalUtil.js';
import IconProvider from '../commons/IconProvider.vue';
import historyAction from './historyAction.json'; // Import the JSON file 
import { linkMixin } from '../../mixins/linkMixin.js';
import { ACTION_TYPES } from '../../constants.js';

export default {
  name: "RecentActions",
  computed: {
    ...mapGetters({
      getLoggedInUserEmailAddress: "email/getLoggedInUserEmailAddress",
      selectedAssignment: "assignments/selectedAssignment",
    }),
    getFilteredRecentActions() {
      const list = this.actionHistory || [];
      console.log(list)
      if (this.filterMode === 'assignment') {
        if (!this.selectedAssignment) {
          return [];
        }
        const selId = String(this.selectedAssignment.viewEventId);
        return list.filter(a => (a.assignmentIds || []).includes(selId));
      }
      return list;
    }
  },
  mixins: [linkMixin],
  components: {
    IconProvider,
  },
  data() {
    return {
      actionHistory: [],
      isLoaded: false,
      isLoading: false,
      currentPage: 0,
      pageSize: 10,
      hasMoreData: true,
      isOpen: false,
      filterMenu: false,
      filterMode: 'all', // 'assignment' | 'all',
      historyAction: historyAction || { linkConfigs: [] },
      pollingInterval: null
    };
  },
  created() {
    EventBus.on('refreshRecentActions', this.refreshRecentActions);
    EventBus.on('toggleRecentActionFilter', this.setFilter);
  },
  methods: {
    getConfigByType(linkType) {
      return this.historyAction?.linkConfigs?.find(
      config => config.config?.externalLinkType === linkType
     )?.config;
    },

    getLinkConfig(action) {
      if (action.action === 'Save to Box') {
        return this.getConfigByType('outlook-box-folder');
      }
      return this.historyAction?.linkConfigs?.[0]?.config;
    },

    getLinkHref(action) {
      if (action.action === 'Save to Box') {
        const config = this.getConfigByType('outlook-box-folder');
        return config ? this.link(config, action.folderId) : '#';
      }
      const config = this.historyAction?.linkConfigs?.[0]?.config;
      return config ? this.link(config, action.assignmentIds[0], action.fileId) : '#';
    },
    formatDate(dateString) {
      return formatDateString(dateString);
    },
    toggleOpen() {
      this.isOpen = !this.isOpen;
      // Need to refresh data to get the latest quote status.
      if (this.isOpen) {
        this.fetchActionHistory();
        // To refresh latest quote status once the quote is created.
        this.startPolling();
      } else {
        this.stopPolling();
      }
    },
    startPolling() {
      // Clear any existing interval
      this.stopPolling();
      // Set up new polling interval (30 seconds)
      this.pollingInterval = setInterval(() => {
        if (this.isOpen && !this.isLoading) {
          this.refreshRecentActions();
        }
      }, 30000); // 30 seconds
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    extendedDescription(action) {
      if (!action) return '';
      let text = action.description;
      if (action.action === 'Create New Quote' && action.engagementStatus === 'FAILURE') {
        text += ' | Quote Creation Failed';
      }
      if (action.action === 'Create New Quote' && action.engagementStatus === 'DUPLICATE') {
        text += ' | Potential Duplicate Found';
      }
      return text;
    },
    setFilter(mode) {
      this.filterMode = mode;
      this.filterMenu = false;
    },
    refreshRecentActions() {
      this.currentPage = 0;
      this.hasMoreData = true;
      this.fetchActionHistory();
    },
    async fetchActionHistory(loadMore = false) {
      this.isLoading = true;
      const userEmail = Office.context.mailbox.userProfile.emailAddress;
      console.log(
        "Fetching action history for user:",
        userEmail,
        "Page:",
        this.currentPage
      );

      if (!userEmail) {
        console.warn("No logged in user email available");
        this.isLoaded = true;
        return;
      }

      if (!loadMore) {
        this.isLoading = true;
      }

      try {
        const response = await fetchRecentActions(
          userEmail,
          this.currentPage,
          this.pageSize
        );

        if (response) {
          const newActions = response;

          if (loadMore) {
            // Append new actions to existing list
            this.actionHistory = [...this.actionHistory, ...newActions];
          } else {
            // Replace with initial load
            this.actionHistory = newActions;
          }

          // Check if we have more data
          this.hasMoreData = newActions.length === this.pageSize;

          if (loadMore) {
            this.currentPage++;
          }
          this.isLoaded = false;
        } else {
          throw new Error("Failed to fetch action history");
        }
      } catch (error) {
        console.error("Error fetching action history:", error);
        if (!loadMore) {
          this.actionHistory = [];
        }
      } finally {
        this.isLoading = false;
        this.isLoaded = true;
      }
    },

    handleScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px buffer

      if (isAtBottom && !this.isLoading && this.hasMoreData) {
        this.currentPage++;
        this.fetchActionHistory(true);
      }
    },

    // formatDate(dateString) {
    //   const date = new Date(dateString);
    //   const today = new Date();
    //   const yesterday = new Date(today);
    //   yesterday.setDate(today.getDate() - 1);

    //   // Reset time to compare dates only
    //   const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    //   const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    //   const yesterdayOnly = new Date(
    //     yesterday.getFullYear(),
    //     yesterday.getMonth(),
    //     yesterday.getDate()
    //   );

    //   const timeString = date.toLocaleTimeString("en-US", {
    //     hour: "numeric",
    //     minute: "2-digit",
    //   });

    //   if (dateOnly.getTime() === todayOnly.getTime()) {
    //     return `Today, ${timeString}`;
    //   } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
    //     return `Yesterday, ${timeString}`;
    //   } else {
    //     // Use existing format for older dates
    //     return date.toLocaleDateString("en-US", {
    //       month: "short",
    //       day: "numeric",
    //       hour: "numeric",
    //       minute: "2-digit",
    //     });
    //   }
    // },
  },
};
</script>

<style scoped>
.list-container {
    min-width: 120px;
    min-height: 80px;
}

.list-item {
  min-height: 49px;
  height: 34px;
  font-family: Calibre-R;
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  letter-spacing: 0px;
  vertical-align: middle;

}

.filter-active {
  background-color: #E6F4EA !important;
  /* light green */
  color: #003f2d !important;
  /* brand green text */
  font-weight: 600;
  border-radius: 0px;
}

.filter-active :deep(.v-list-item-title) {
  color: inherit;
}

.v-list .v-list-item:hover {
  background-color: #F1F5F3;
  border-radius: 0px;
  /* subtle hover */
}

.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  list-style: none;
  cursor: pointer;
  user-select: none;
  background: #e3e3e3;
  padding: 12px 16px;
  border-radius: 0;
  margin: 0;
}

.summary-left {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.filter-btn :deep(svg) {
  width: 20px;
  height: 20px;
}

.filter-label {
  font-family: Calibre-R;
  font-weight: 500;
  font-size: 12px;
  line-height: 21px;
  color: #1A1A1A;
  padding: 6px 6px;
  border-radius: 4px;
  background-color: #F5F5F5;
  cursor: pointer;
  border: 1px solid #E0E0E0;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  min-width: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.filter-label:hover {
  background-color: #EEEEEE;
  border-color: #D0D0D0;
}

.filter-arrow {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.recent-actions {
  margin-bottom: 1rem;
  background: #ffffff;
  border-radius: 0;
}

.recent-actions h2 {
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: black;
}

.recent-actions-card {
  background: #fff;
  padding: 4px 0;
  overflow: hidden;
}

.actions-list {
  max-height: 300px;
  overflow-y: auto;
  border-radius: 0;
  padding-right: 8px;
}

.action-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.75rem 1rem;
  border: 1px solid #e5e9ed;
  transition: background-color 0.15s ease;
  margin: 8px 0px;
  border-radius: 4px;
  box-shadow: 0px 1px 4px 0px #0000001F;
  box-shadow: 0px 1px 4px 0px #00000040;
}

.action-item:first-child {
  margin-top: 0;
}

.action-item:last-child {
  margin-bottom: 0;
}

.action-item:hover {
  background-color: #f9fafb;
}

.action-content {
  flex: 1;
  min-width: 0;
}

.action-title {
  font-weight: 600;
  font-size: 0.875rem;
  color: #111827;
  margin-bottom: 0.25rem;
  line-height: 1.25;
  font-family: Calibre-R;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0px;
  color: #000000;
}

/* Style for anchor tags within action-title */
.action-title a {
  color: green; /* Green color */
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.action-title a:hover {
  color: #003F2D; /* Darker green on hover */
  text-decoration: underline;
}

.action-title a:focus {
  color: #003F2D;
  outline: 2px solid #003F2D;
  outline-offset: 2px;
  border-radius: 2px;
}

.action-description {
  color: #6b7280;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Calibre-R-Medium;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -1.5%;
  color: #1A1A1A;
}

.action-date {
  color: #1A1A1AA6;
  white-space: nowrap;
  margin-left: 1rem;
  flex-shrink: 0;
  font-family: Calibre-R-Medium;
  font-weight: 500;
  font-style: Medium;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: 0px;

}

.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.no-actions-message {
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  padding: 1rem;
  color: #777;
  text-align: center;
  border: 1px solid;
  border-color: rgb(240 243 245);
}

/* Custom scrollbar */
.actions-list::-webkit-scrollbar {
  width: 6px;
}

.actions-list::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.actions-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.actions-list::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
@media (max-width: 320px) {
  .recent-actions-card {
    padding: 2px 0;
  }
  
  .actions-list {
    max-height: 250px;
    overflow-y: auto;
    padding-right: 4px;
  }
  
  .action-item {
    flex-direction: column;
    align-items: flex-start;
    padding: 0.5rem 0.75rem;
    margin: 6px 0px;
  }

  .action-content {
    width: 100%;
  }

  .action-date {
    margin-left: 0;
    margin-top: 4px;
    font-size: 12px!important;
  }
  
  .action-description {
    margin-top: 4px;
    font-size: 13px!important;
    line-height: 1.3!important;
    white-space: normal;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;
  }
  
  .action-title {
    font-size: 13px!important;
    line-height: 1.3!important;
    white-space: normal;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;
  }

.additional-actions {
  background: #ffffff;
  margin: 0;
  padding: 0;
}

.inline-engagement {
  display: inline;
  white-space: nowrap;
}

.engagement-link {
  color: green;
  text-decoration: none;
  font-weight: 500;
  display: inline;
}

  .additional-actions-title{
    font-size: 14px!important;
  }

}

</style>
